import { useEffect, useMemo, useState } from "react";
import {
    fetchSalesByCategoryGroup,
    fetchTotalBudgetByCategoryGroup,
} from "../services/orderAnalytics.service";
import { getProRataProgress } from '../helpers/helper';

const EMPTY_STATE = {
    year: new Date().getFullYear(),
    achieved: 0,
    budget: 0,
};

export function useSalesVsBudget({
    categoryId,
    dateParams,
    isDateRangeIncomplete,
}) {
    const [data, setData] = useState(EMPTY_STATE);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const { from, to } = dateParams || {};

    useEffect(() => {
        if (isDateRangeIncomplete || !from || !to) {
            setData(EMPTY_STATE);
            setIsLoading(false);
            return;
        }

        let isMounted = true;

        const load = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const year = from.slice(0, 4);

                const [salesList, budgetRes] = await Promise.all([
                    fetchSalesByCategoryGroup({ from, to }),
                    fetchTotalBudgetByCategoryGroup({ year }),
                ]);

                if (!isMounted) return;

                const achieved =
                    salesList.find((i) => i.id === categoryId)?.total || 0;

                const budget = budgetRes?.[categoryId] || 0;

                setData({ year, achieved, budget });
            } catch (err) {
                if (isMounted) {
                    console.error("Sales vs Budget error", err);
                    setError("Unable to load sales data");
                    setData(EMPTY_STATE);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        load();

        return () => {
            isMounted = false;
        };
    }, [from, to, isDateRangeIncomplete, categoryId]);

    const computed = useMemo(() => {
        if (!data.budget) {
            return {
                achievedPercent: 0,
                deltaPercent: 0,
            };
        }

        const achievedPercent = (data.achieved / data.budget) * 100;
        const { elapsedDays, totalDays } = getProRataProgress(
            dateParams?.filterType,
            to,
            data.year
        );
        const proRataTarget = data.budget * (elapsedDays / totalDays);
        const deltaPercent = proRataTarget > 0 ? (data.achieved / proRataTarget) * 100 : 0;

        return {
            achievedPercent,
            deltaPercent,
        };
    }, [data, to, dateParams]);

    return {
        data,
        ...computed,
        isLoading,
        error,
    };
}
